import _extends from "@babel/runtime/helpers/extends";

/** @jsx jsx */
import { forwardRef, memo } from 'react';
import { jsx } from '@emotion/core';
import BaseItem from '../internal/components/base-item';
import { useBlurOnMouseDown } from '../internal/hooks/use-blur-on-mouse-down';
import { linkItemCSS } from '../internal/styles/menu-item/link-item';

const preventEvent = e => {
  e.preventDefault();
};
/**
 * __Link item__
 *
 * A link item is used to populate a menu with items that are links.
 *
 * - [Examples](https://atlaskit.atlassian.com/packages/design-system/menu/docs/link-item)
 * - [Code](https://atlaskit.atlassian.com/packages/design-system/menu)
 */


const LinkItem = /*#__PURE__*/memo( /*#__PURE__*/forwardRef( // Type needed on props to extract types with extract react types.
({
  href,
  ...rest
}, ref) => {
  const {
    children,
    cssFn = () => ({}),
    description,
    iconAfter,
    iconBefore,
    isDisabled = false,
    isSelected = false,
    onClick,
    testId,
    overrides,
    onMouseDown,
    shouldTitleWrap,
    shouldDescriptionWrap,
    ...others
  } = rest;
  const onMouseDownHandler = useBlurOnMouseDown(onMouseDown);

  if (!children) {
    return null;
  }

  return jsx("a", _extends({
    ref: ref,
    css: [// eslint-disable-next-line @repo/internal/react/consistent-css-prop-usage
    linkItemCSS(isDisabled, isSelected), // eslint-disable-next-line @repo/internal/react/consistent-css-prop-usage
    cssFn({
      isSelected,
      isDisabled
    })],
    draggable: false,
    href: isDisabled ? undefined : href,
    "data-testid": testId,
    onDragStart: preventEvent,
    onMouseDown: isDisabled ? preventEvent : onMouseDownHandler,
    onClick: isDisabled ? preventEvent : onClick,
    "aria-current": isSelected ? 'page' : undefined,
    "aria-disabled": isDisabled
  }, others), jsx(BaseItem // eslint-disable-next-line @repo/internal/react/no-unsafe-overrides
  , {
    overrides: overrides,
    iconBefore: iconBefore,
    iconAfter: iconAfter,
    description: description,
    shouldTitleWrap: shouldTitleWrap,
    shouldDescriptionWrap: shouldDescriptionWrap
  }, children));
}));
export default LinkItem;